/**
 * Certainty of evidence (GRADE) contract (F06, roadmap §6 phase 1): the overview of every
 * outcome, one outcome's assessment state, the AI draft, edits and sign-off. Re-exported from
 * index.ts.
 *
 * White-label (CONT-058): responses say whether a draft was drafted with AI, never which model;
 * the provider and model ID stay in the database, the model_call record and audit exports.
 *
 * Owner: grade (F06). Implements the HTTP side of CERT-090 to CERT-106 and CERT-136 to CERT-139.
 */
import type {
  Certainty,
  GradeAssessment,
  GradeSignals,
  GradeUpgrades,
  OutcomeDefinition,
  UpgradeSignals,
} from '@bioterafi/evidence-model';
import { pagePath } from './paths';

export const GRADE_ROUTES = {
  /** GET -> 200 GradeOverviewResponse, member(viewer) (E170; CERT-139). */
  gradeOverview: (slug: string) => `${pagePath(slug)}/grade`,
  /** GET -> 200 GradeStateResponse, member(viewer) (E73; CERT-104, CERT-106). */
  gradeState: (slug: string, outcomeId: string) => `${pagePath(slug)}/grade/${encodeURIComponent(outcomeId)}`,
  /**
   * POST GradeDraftRequest -> 201 GradeStateResponse, an AI draft (E41; CERT-090 to CERT-099);
   * PUT GradeEditRequest -> 200 GradeStateResponse, a person's edit (E42; CERT-101, CERT-103). member(editor).
   */
  gradeDraft: (slug: string, outcomeId: string) => `${pagePath(slug)}/grade/${encodeURIComponent(outcomeId)}/draft`,
  /** POST GradeSignOffRequest -> 200 GradeStateResponse, member(editor) (E43; CERT-102, CERT-137). */
  gradeSignOff: (slug: string, outcomeId: string) => `${pagePath(slug)}/grade/${encodeURIComponent(outcomeId)}/sign-off`,
} as const;

export type GradeDomainName = keyof GradeAssessment['domains'];

/** The pending draft of an outcome (CERT-094, CERT-101). */
export interface GradeDraft {
  outcomeId: string;
  domains: GradeAssessment['domains'];
  /** Why the AI departed from a signal, per domain (CERT-092); empty for a person's draft. */
  departures: Partial<Record<GradeDomainName, string>>;
  bodyOfEvidence: NonNullable<GradeAssessment['bodyOfEvidence']>;
  upgrades: GradeUpgrades;
  /** Derived (CERT-061), never typed by hand. */
  certainty: Certainty;
  /** True when the AI drafted it; it stays true after a person edits it (CERT-106 "Drafted with AI"). */
  draftedWithAi: boolean;
  /** True when a person has saved it since the AI drafted it. */
  editedByPerson: boolean;
  revision: number;
  updatedAt: string;
}

/** What the engine knows about the outcome's pre-specified analysis (CERT-071). */
export interface GradeOutcomeContext {
  /** False when the outcome cannot be pooled yet; `problem` says why. */
  estimable: boolean;
  problem: string | null;
  /** Studies contributing to the pre-specified analysis. */
  studies: number;
  /** CERT-130 on the contributing studies. */
  design: 'randomised' | 'non_randomised' | 'mixed';
  /** CERT-131: the start for each design the reviewer may choose. */
  startingCertainty: { randomised: 'high'; non_randomised: 'high' | 'low' };
  /** CERT-138: false when no contributing study has a signed-off risk-of-bias assessment. */
  robAssessed: boolean;
  /** Display-scale effect of the pre-specified analysis, for the screen's heading. */
  effect: { measure: string; estimate: number; lower: number; upper: number; level: number } | null;
}

export interface GradeStateResponse {
  outcome: Pick<OutcomeDefinition, 'id' | 'name' | 'role' | 'definition' | 'timepoint'>;
  /** The signed-off assessment, which readers see (CERT-104, CERT-112). */
  current: GradeAssessment | null;
  draft: GradeDraft | null;
  context: GradeOutcomeContext;
  /** Null when the outcome is not estimable. */
  signals: GradeSignals | null;
  upgradeSignals: UpgradeSignals | null;
  /** The signed-off assessment judged different evidence from the current (CERT-102, CERT-105). */
  stale: boolean;
  /** The caller's role allows drafting, editing and signing off (CERT-100). */
  canEdit: boolean;
}

/** POST gradeDraft. `design` is required when the body of evidence is mixed (CERT-130). */
export interface GradeDraftRequest {
  design?: 'randomised' | 'non_randomised';
}

/** PUT gradeDraft. `revision` is 0 to start a draft when none exists (CERT-103). */
export interface GradeEditRequest {
  domains: GradeAssessment['domains'];
  bodyOfEvidence: { design: 'randomised' | 'non_randomised' };
  upgrades: GradeUpgrades;
  revision: number;
}

export interface GradeSignOffRequest {
  revision: number;
}

export interface GradeOverviewItem {
  outcomeId: string;
  name: string;
  role: OutcomeDefinition['role'];
  /** Studies contributing to the pre-specified analysis; 0 when the outcome has no usable data. */
  studies: number;
  hasDraft: boolean;
  /** Signed-off certainty, or null. */
  certainty: Certainty | null;
  signedOffAt: string | null;
  stale: boolean;
}

/** GET gradeOverview: protocol outcomes, primary first (CERT-139). */
export interface GradeOverviewResponse {
  outcomes: GradeOverviewItem[];
  canEdit: boolean;
}
